import { products } from '@/data/productData';

export interface GiftPromotion {
  id: string;
  name: string;
  min_subtotal: number;
  gift_product_id: number;
  gift_quantity: number;
  discount_percentage: number;
  active: boolean;
  starts_at: string | null;
  ends_at: string | null;
}

export interface GiftLine {
  product_id: number;
  name: string;
  image: string;
  quantity: number;
  price: number;
  original_price: number;
  discount_percentage: number;
  promotion_id: string;
  promotion_name: string;
  is_gift: true;
}

export const isPromotionRunning = (promo: GiftPromotion, now: Date = new Date()): boolean => {
  if (!promo.active) return false;
  if (promo.starts_at && new Date(promo.starts_at).getTime() > now.getTime()) return false;
  if (promo.ends_at && new Date(promo.ends_at).getTime() < now.getTime()) return false;
  return true;
};

/**
 * Derive the free / discounted gift lines a cart currently qualifies for.
 * `subtotal` must be the paid-items subtotal (gift lines excluded).
 */
export const computeGiftLines = (
  subtotal: number,
  promotions: GiftPromotion[],
  getProductPrice: (productId: number) => number
): GiftLine[] => {
  const now = new Date();
  const best = new Map<number, GiftLine>();

  promotions
    .filter((promo) => isPromotionRunning(promo, now))
    .filter((promo) => subtotal >= Number(promo.min_subtotal || 0))
    .forEach((promo) => {
      const productId = Number(promo.gift_product_id);
      const product = products.find((p) => p.id === productId);
      const basePrice = getProductPrice(productId) || product?.price || 0;
      const pct = Math.max(0, Math.min(100, Number(promo.discount_percentage) || 0));
      const unitPrice = Math.round(basePrice * (1 - pct / 100) * 100) / 100;

      const line: GiftLine = {
        product_id: productId,
        name: product?.name || `Product ${productId}`,
        image: product?.image || '',
        quantity: Math.max(1, Number(promo.gift_quantity) || 1),
        price: unitPrice,
        original_price: basePrice,
        discount_percentage: pct,
        promotion_id: promo.id,
        promotion_name: promo.name,
        is_gift: true,
      };

      const existing = best.get(productId);
      // Keep the most generous promotion per product
      if (!existing || line.discount_percentage > existing.discount_percentage) {
        best.set(productId, line);
      }
    });

  return Array.from(best.values());
};

export const giftLinesTotal = (lines: GiftLine[]): number =>
  lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
